// src/pages/builder/TopicLibraryActions.js
//
// The Topic Library's per-row pop-ups:
//   <AddToStateDialog>   ➕ Add to state — state, grade, standard number;
//                        puts the topic on that state's student AND
//                        educator page (its video, song, game and
//                        description come from the library).
//   <DeleteTopicDialog>  🗑 Delete — shows which pages use the topic and
//                        lets you put another topic in its place on them,
//                        take it off them, or (when another record has the
//                        very same slug) leave them as they are.
// The page writes live in services/topicPagesService.js.

import React, { useEffect, useMemo, useState } from "react";
import { STATE_GROUPS, stateName } from "../../config/geography";
import { exactKey } from "../../utils/topicKey";
import { addTopicToState, pagesUsingSlug, replaceTopicOnPages } from "../../services/topicPagesService";
import { deleteLibraryTopic } from "../../services/topicLibraryService";
import "./topicLibraryActions.css";

const GRADES = ["k", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th"];
const gradeLabel = (g) => (g === "k" ? "Kindergarten" : `${g} grade`);
const looseTitle = (t) => String(t || "").toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]/g, "");
const SUBJECT_NAMES = { math: "Math", science: "Science", socialstudies: "Social Studies", other: "Other" };

function Modal({ title, onClose, busy, children, footer }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);
  return (
    <div className="tla-overlay" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="tla-box">
        <header className="tla-head">
          <h3>{title}</h3>
          <button type="button" className="tla-x" onClick={onClose} disabled={busy} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="tla-body">{children}</div>
        {footer && <footer className="tla-foot">{footer}</footer>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ➕ Add to state                                                     */
/* ------------------------------------------------------------------ */

export function AddToStateDialog({ topic, onClose, onDone }) {
  const [state, setState] = useState("");
  const [grade, setGrade] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [onPages, setOnPages] = useState(null);

  useEffect(() => {
    let alive = true;
    pagesUsingSlug(topic.slug)
      .then((p) => alive && setOnPages(p.filter((x) => x.view === "student")))
      .catch(() => alive && setOnPages([]));
    return () => {
      alive = false;
    };
  }, [topic.slug]);

  const already = useMemo(
    () => (onPages || []).find((p) => p.state === state.toLowerCase() && p.grade === grade && p.subject === (topic.subject || "science")),
    [onPages, state, grade, topic.subject]
  );

  const go = async () => {
    if (!state || !grade) {
      setError("Pick a state and a grade.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await addTopicToState({ topic, state, grade, standardNumber: code });
      setResult(r);
      onDone?.(r);
    } catch (e) {
      setError(e?.code === "permission-denied" ? "Permission denied — this needs an admin account." : e?.message || "Couldn't add it.");
    } finally {
      setBusy(false);
    }
  };

  const pageName = `${stateName(state)} · ${gradeLabel(grade)} · ${SUBJECT_NAMES[topic.subject] || topic.subject}`;

  return (
    <Modal
      title={`➕ Add “${topic.title || topic.slug}” to a state`}
      onClose={onClose}
      busy={busy}
      footer={
        result ? (
          <button type="button" className="lb-btn" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="lb-btn lb-btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="lb-btn" onClick={go} disabled={busy || !state || !grade}>
              {busy ? "Adding…" : already ? "Update standard number" : "Add to both pages"}
            </button>
          </>
        )
      }
    >
      {result ? (
        <div className="tla-done">
          <p>
            ✓ <b>{pageName}</b>
          </p>
          <ul>
            {result.added.map((id) => (
              <li key={id}>Added to {id}</li>
            ))}
            {result.created.map((id) => (
              <li key={id}>Made a new page: {id}</li>
            ))}
            {result.updated.map((id) => (
              <li key={id}>Standard number updated on {id}</li>
            ))}
            {!result.added.length && !result.created.length && !result.updated.length && <li>It was already there with that standard number — nothing changed.</li>}
          </ul>
          <p className="tla-muted">
            Its video, song, sing-along, game and description come from this library record. Pages put topics in standard-number order, so it shows in its place.
            Backups: layoutBackups/…__{result.stamp}
          </p>
        </div>
      ) : (
        <>
          <div className="tla-grid">
            <label className="tla-field">
              State
              <select value={state} onChange={(e) => setState(e.target.value)}>
                <option value="">Choose…</option>
                {STATE_GROUPS.filter((g) => g.codes.length).map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.codes.map((c) => (
                      <option key={c} value={c.toLowerCase()}>
                        {stateName(c)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className="tla-field">
              Grade
              <select value={grade} onChange={(e) => setGrade(e.target.value)}>
                <option value="">Choose…</option>
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    {gradeLabel(g)}
                  </option>
                ))}
              </select>
            </label>
            <label className="tla-field tla-wide">
              Standard number
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. K.7a or K-ESS2-1" maxLength={60} />
              <small>That state's own code — it's what orders the topics on the page. Optional.</small>
            </label>
          </div>
          {state && grade && (
            <p className={`tla-note ${already ? "is-warn" : ""}`}>
              {already
                ? `Already on ${pageName}${already.entry.standardNumber ? ` as ${already.entry.standardNumber}` : ""}. Adding again just updates its standard number.`
                : `Goes on the ${pageName} student and educator pages.`}
            </p>
          )}
          {topic.status !== "published" && (
            <p className="tla-note is-warn">This topic is a draft — it's added now, but students only see it once you publish it.</p>
          )}
          {onPages && onPages.length > 0 && (
            <details className="tla-where">
              <summary>Already on {onPages.length} page{onPages.length === 1 ? "" : "s"}</summary>
              <ul>
                {onPages.map((p) => (
                  <li key={p.id}>
                    {stateName(p.state)} · {gradeLabel(p.grade)} {p.entry.standardNumber ? `· ${p.entry.standardNumber}` : ""}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {error && <p className="tla-error">{error}</p>}
        </>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* 🗑 Delete, with something in its place                              */
/* ------------------------------------------------------------------ */

export function DeleteTopicDialog({ topic, topics, onClose, onDeleted }) {
  const [pages, setPages] = useState(null);
  const [mode, setMode] = useState(""); // "replace" | "remove" | "leave"
  const [pick, setPick] = useState("");
  const [find, setFind] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const id = topic.id || topic.slug;
  // another record with the very same slug keeps serving those pages
  const twin = useMemo(
    () => topics.find((t) => (t.id || t.slug) !== id && exactKey(t.slug) === exactKey(topic.slug) && t.status !== "merged"),
    [topics, topic, id]
  );
  const candidates = useMemo(() => {
    const tk = looseTitle(topic.title);
    const q = find.trim().toLowerCase();
    return topics
      .filter((t) => (t.id || t.slug) !== id && t.status !== "merged" && exactKey(t.slug) !== exactKey(topic.slug))
      .filter((t) => (t.subject || "other") === (topic.subject || "other"))
      .filter((t) => !q || `${t.title} ${t.slug}`.toLowerCase().includes(q))
      .map((t) => ({ t, same: tk && looseTitle(t.title) === tk }))
      .sort((a, b) => Number(b.same) - Number(a.same) || Number(b.t.status === "published") - Number(a.t.status === "published") || String(a.t.title).localeCompare(String(b.t.title)))
      .slice(0, 60);
  }, [topics, topic, id, find]);

  useEffect(() => {
    let alive = true;
    pagesUsingSlug(topic.slug, { fresh: true })
      .then((p) => {
        if (!alive) return;
        setPages(p);
        if (!p.length || twin) setMode(twin ? "leave" : "");
        else setMode("replace");
      })
      .catch(() => alive && setPages([]));
    return () => {
      alive = false;
    };
  }, [topic.slug, twin]);

  useEffect(() => {
    if (!pick && candidates[0]?.same) setPick(candidates[0].t.id || candidates[0].t.slug);
  }, [candidates, pick]);

  const target = topics.find((t) => (t.id || t.slug) === pick) || null;
  const pageCount = pages?.length || 0;
  const studentPages = (pages || []).filter((p) => p.view === "student");

  const go = async () => {
    if (pageCount && mode === "replace" && !target) {
      setError("Pick the topic to put in its place.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      let pageResult = null;
      if (pageCount && mode === "replace") pageResult = await replaceTopicOnPages({ fromSlug: topic.slug, to: target, pageIds: pages.map((p) => p.id) });
      if (pageCount && mode === "remove") pageResult = await replaceTopicOnPages({ fromSlug: topic.slug, to: null, pageIds: pages.map((p) => p.id) });
      await deleteLibraryTopic(id);
      setDone({ pageResult });
      onDeleted?.(id);
    } catch (e) {
      setError(e?.code === "permission-denied" ? "Permission denied — this needs an admin account." : e?.message || "Couldn't delete it.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`🗑 Delete “${topic.title || topic.slug}”`}
      onClose={onClose}
      busy={busy}
      footer={
        done ? (
          <button type="button" className="lb-btn" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="lb-btn lb-btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="lb-btn lb-btn-danger" onClick={go} disabled={busy || pages === null}>
              {busy
                ? "Working…"
                : !pageCount || mode === "leave"
                ? "Delete topic"
                : mode === "replace"
                ? `Replace on ${studentPages.length} state page${studentPages.length === 1 ? "" : "s"} & delete`
                : `Take off ${studentPages.length} state page${studentPages.length === 1 ? "" : "s"} & delete`}
            </button>
          </>
        )
      }
    >
      {done ? (
        <div className="tla-done">
          <p>✓ Deleted <b>{topic.slug}</b> from the library.</p>
          {done.pageResult && (
            <p>
              {done.pageResult.replaced ? `${done.pageResult.replaced} page entr${done.pageResult.replaced === 1 ? "y" : "ies"} now show “${target?.title || target?.slug}”. ` : ""}
              {done.pageResult.removed ? `${done.pageResult.removed} entr${done.pageResult.removed === 1 ? "y was" : "ies were"} taken off (or the page already had the new topic). ` : ""}
              Backups: layoutBackups/…__{done.pageResult.stamp}
            </p>
          )}
          <p className="tla-muted">Its game and sing-along timings were left in place — nothing else uses them under this slug unless you add it again.</p>
        </div>
      ) : pages === null ? (
        <p className="tla-muted">Checking which pages use it…</p>
      ) : (
        <>
          <p>
            <code>{topic.slug}</code> · {topic.status === "published" ? "live" : topic.status || "draft"}
            {pageCount ? (
              <>
                {" "}
                · on <b>{studentPages.length}</b> state page{studentPages.length === 1 ? "" : "s"} ({pageCount} counting educator pages)
              </>
            ) : (
              " · not on any page"
            )}
          </p>

          {pageCount > 0 && (
            <>
              <details className="tla-where">
                <summary>Which pages</summary>
                <ul>
                  {studentPages.map((p) => (
                    <li key={p.id}>
                      {stateName(p.state)} · {gradeLabel(p.grade)} {p.entry.standardNumber ? `· ${p.entry.standardNumber}` : ""}
                    </li>
                  ))}
                </ul>
              </details>

              <div className="tla-choices">
                {twin && (
                  <label className={`tla-choice ${mode === "leave" ? "is-on" : ""}`}>
                    <input type="radio" checked={mode === "leave"} onChange={() => setMode("leave")} />
                    <span>
                      <b>Leave the pages as they are</b>
                      <small>
                        Another record has the same slug (<code>{twin.slug}</code>{twin.title ? ` — ${twin.title}` : ""}), so those pages simply show that one.
                      </small>
                    </span>
                  </label>
                )}
                <label className={`tla-choice ${mode === "replace" ? "is-on" : ""}`}>
                  <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} />
                  <span>
                    <b>Put another topic in its place</b>
                    <small>Schools using it see the topic you pick, with the same standard number and position.</small>
                  </span>
                </label>
                {mode === "replace" && (
                  <div className="tla-pick">
                    <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search topics…" />
                    <select size={7} value={pick} onChange={(e) => setPick(e.target.value)}>
                      {candidates.map(({ t, same }) => (
                        <option key={t.id || t.slug} value={t.id || t.slug}>
                          {same ? "★ " : ""}
                          {t.title || t.slug} — {t.slug} {t.status === "published" ? "" : "(draft)"}
                        </option>
                      ))}
                    </select>
                    <small className="tla-muted">★ same title. A draft only shows on pages once it's published.</small>
                  </div>
                )}
                <label className={`tla-choice ${mode === "remove" ? "is-on" : ""}`}>
                  <input type="radio" checked={mode === "remove"} onChange={() => setMode("remove")} />
                  <span>
                    <b>Take it off those pages</b>
                    <small>The topic card disappears from those states. Nothing goes in its place.</small>
                  </span>
                </label>
              </div>
            </>
          )}
          <p className="tla-muted">Every page is backed up before it changes.</p>
          {error && <p className="tla-error">{error}</p>}
        </>
      )}
    </Modal>
  );
}
